import { useCallback, useMemo, useState } from 'react';
import { createContainer } from 'react-tracked';

import { useEvent } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';

import type { CustomMetric } from '../api/types.js';
import {
  initialSearchParams,
  useCreateCustomMetric,
  useCustomMetrics,
  useDeleteCustomMetric,
  useUpdateCustomMetric,
} from '../queries/index.js';

export type MetricDraft = Pick<
  CustomMetric,
  'id' | 'label' | 'metricType' | 'isUsed' | 'isDateValidationIgnored'
>;

const initialMetricDraft: MetricDraft = {
  id: 'draft',
  label: '',
  metricType: null,
  isDateValidationIgnored: false,
};

const initialDrawerState = {
  isOpen: false,
  isDeletePopoverOpen: false,
  searchValue: '',
  selectedMetricDraft: initialMetricDraft,
  isDrawerAnimating: false,
};

const useValue = () => {
  const { t } = useTranslation('@myn/import-hub');
  const [
    {
      isOpen,
      isDeletePopoverOpen,
      searchValue,
      selectedMetricDraft,
      isDrawerAnimating,
    },
    setState,
  ] = useState(initialDrawerState);

  const { data: metaData } = useCustomMetrics(initialSearchParams, {
    enabled: isOpen,
  });

  const {
    data: { items: customMetrics },
    isLoading,
  } = useCustomMetrics({ search: searchValue }, { enabled: isOpen });

  const {
    totalItems: metricsTotalCount,
    additional: { maxCount: metricsMaxCount, canBeCreated: canMetricBeCreated },
  } = metaData;

  const isAddMetricMode = selectedMetricDraft.id === initialMetricDraft.id;

  const getMetricById = useEvent((id: string) => {
    const customMetricsWithDraft = [initialMetricDraft, ...customMetrics];
    const metric = customMetricsWithDraft.find(
      (customMetric) => customMetric.id === id,
    );

    return metric ?? null;
  });

  const selectMetric = useCallback(
    (id: string) => {
      const metricToSelect = getMetricById(id);
      if (metricToSelect) {
        setState((prev) => ({ ...prev, selectedMetricDraft: metricToSelect }));
      }
    },
    [getMetricById],
  );

  const searchMetrics = useCallback((inputValue: string) => {
    setState((prev) => ({ ...prev, searchValue: inputValue }));
  }, []);

  const openDrawer = useEvent(() => {
    setState((prev) => ({ ...prev, isOpen: true, isDrawerAnimating: true }));
    if (customMetrics.length) {
      selectMetric(customMetrics[0].id);
    }
  });

  const onDrawerAnimationEnd = useCallback(() => {
    setState((prev) => ({ ...prev, isDrawerAnimating: false }));
  }, []);

  const closeDrawer = useCallback(() => {
    setState(initialDrawerState);
  }, []);

  const openDeletePopover = useCallback(() => {
    setState((prev) => ({ ...prev, isDeletePopoverOpen: true }));
  }, []);

  const closeDeletePopover = useCallback(() => {
    setState((prev) => ({ ...prev, isDeletePopoverOpen: false }));
  }, []);

  const openMetricForUpdate = useCallback(
    (id: string) => {
      openDrawer();
      selectMetric(id);
    },
    [openDrawer, selectMetric],
  );

  const openMetricForCreate = useCallback(() => {
    openMetricForUpdate(initialMetricDraft.id);
  }, [openMetricForUpdate]);

  const openMetricForDelete = useCallback(
    (id: string) => {
      openMetricForUpdate(id);
      openDeletePopover();
    },
    [openMetricForUpdate, openDeletePopover],
  );

  const updateDraftMetric = useCallback((value: Partial<MetricDraft>) => {
    setState((prev) => ({
      ...prev,
      selectedMetricDraft: { ...prev.selectedMetricDraft, ...value },
    }));
  }, []);

  const { mutateAsync: createMetric } = useCreateCustomMetric();
  const { mutateAsync: updateMetric } = useUpdateCustomMetric({
    search: searchValue,
  });
  const { mutateAsync: deleteMetric } = useDeleteCustomMetric({
    search: searchValue,
  });

  const submitCreateMetric = useEvent(async () => {
    if (selectedMetricDraft) {
      const updateCreatedMetricId = (id: string) => updateDraftMetric({ id });
      try {
        await createMetric({
          payload: selectedMetricDraft,
          onSuccess: updateCreatedMetricId,
        });
        enqueueSnackbar({
          variant: 'success',
          title: t('Custom metric added'),
          subTitle: t('Your custom metric has been added successfully.'),
          textLayout: 'multiline',
          removable: true,
        });
      } catch {
        /* empty */
      }
    }
  });

  const submitUpdateMetric = useEvent(async () => {
    if (selectedMetricDraft) {
      try {
        await updateMetric({
          id: selectedMetricDraft.id,
          payload: selectedMetricDraft,
        });
        enqueueSnackbar({
          variant: 'success',
          title: t('Custom metric updated'),
          subTitle: t('Your custom metric has been updated successfully.'),
          textLayout: 'multiline',
          removable: true,
        });
      } catch {
        /* empty */
        selectMetric(selectedMetricDraft.id);
      }
    }
  });

  const submitDeleteMetric = useEvent(async () => {
    const firstRemainingMetric = customMetrics.find(
      (metric) => metric.id !== selectedMetricDraft.id,
    );
    try {
      await deleteMetric(selectedMetricDraft.id);
      setState((prev) => ({
        ...prev,
        selectedMetricDraft: firstRemainingMetric ?? initialMetricDraft,
      }));
      enqueueSnackbar({
        variant: 'success',
        title: t('Custom metric deleted'),
        subTitle: t('Your custom metric has been deleted successfully.'),
        textLayout: 'multiline',
        removable: true,
      });
    } catch {
      setState((prev) => ({ ...prev, selectedMetricDraft }));
    }
  });

  return useMemo(() => {
    return [
      {
        isOpen,
        isLoading,
        isDeletePopoverOpen,
        isAddMetricMode,
        searchValue,
        customMetrics,
        metricsMaxCount,
        metricsTotalCount,
        canMetricBeCreated,
        selectedMetricDraft,
        openDrawer,
        closeDrawer,
        searchMetrics,
        selectMetric,
        openDeletePopover,
        closeDeletePopover,
        openMetricForCreate,
        openMetricForUpdate,
        openMetricForDelete,
        isDrawerAnimating,
        onDrawerAnimationEnd,
        updateDraftMetric,
        submitCreateMetric,
        submitUpdateMetric,
        submitDeleteMetric,
      },
      () => {
        throw new Error('Use functions in the state');
      },
    ] as const;
  }, [
    isOpen,
    isLoading,
    isDeletePopoverOpen,
    isAddMetricMode,
    searchValue,
    customMetrics,
    metricsMaxCount,
    metricsTotalCount,
    canMetricBeCreated,
    selectedMetricDraft,
    openDrawer,
    closeDrawer,
    searchMetrics,
    selectMetric,
    openMetricForCreate,
    openMetricForUpdate,
    openMetricForDelete,
    isDrawerAnimating,
    onDrawerAnimationEnd,
    openDeletePopover,
    closeDeletePopover,
    updateDraftMetric,
    submitCreateMetric,
    submitUpdateMetric,
    submitDeleteMetric,
  ]);
};

export const {
  Provider: CustomMetricsDrawerProvider,
  useTrackedState: useCustomMetricsDrawer,
} = createContainer(useValue);
